import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import POSTerminal from '@paytecag/ecritf';

// PrivaPay / PayTec card terminal via "KIT via Cloud". One connection per browser (reception device).
const STORAGE_KEY = 'paytec_pairing_info';
const TerminalCtx = createContext(null);

const loadPairing = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') || undefined; } catch { return undefined; }
};

export const CardTerminalProvider = ({ children }) => {
  const trmRef = useRef(null);
  const pendingRef = useRef(null);
  const [paired, setPaired] = useState(!!loadPairing());
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState('');
  const [model, setModel] = useState('');
  const [chargeAmount, setChargeAmount] = useState(0);

  const finish = useCallback((result) => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setBusy(false);
    if (p) p.resolve(result);
  }, []);

  const build = useCallback(() => {
    if (trmRef.current) return trmRef.current;
    const trm = new POSTerminal(loadPairing(), {
      TrmLng: 'de',
      AutoConnect: true,
      AutoReconnect: true,
      AutoConfirm: false,
      OnConnected: () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(trm.getPairingInfo()));
        setPaired(true); setConnected(true); setLastError('');
        try { setModel(`${trm.getDeviceModelName() || ''} ${trm.getTerminalID() || ''}`.trim()); } catch { /* noop */ }
        const p = pendingRef.current;
        if (p?.kind === 'pair') { pendingRef.current = null; p.resolve({ ok: true }); }
      },
      OnDisconnected: () => setConnected(false),
      OnPairingFailed: () => {
        setLastError('Kopplung fehlgeschlagen');
        const p = pendingRef.current;
        if (p?.kind === 'pair') { pendingRef.current = null; p.resolve({ ok: false, error: 'Kopplung fehlgeschlagen – Code prüfen' }); }
      },
      OnActivationSucceeded: () => { const p = pendingRef.current; if (p?.kind === 'charge' && p.waitActivation) { p.waitActivation = false; p.start(); } },
      OnActivationFailed: () => finish({ approved: false, reason: 'Terminal konnte nicht aktiviert werden' }),
      OnActivationTimedOut: () => finish({ approved: false, reason: 'Terminal-Aktivierung Zeitüberschreitung' }),
      OnTransactionApproved: (rsp) => {
        // Confirm ourselves: if reception already cancelled, roll back so the card is never charged.
        if (pendingRef.current?.kind === 'charge' && !pendingRef.current.abandoned) {
          pendingRef.current.rsp = rsp;
          trm.confirmTransaction({});
        } else {
          try { trm.rollbackTransaction({}); } catch { /* noop */ }
        }
      },
      OnTransactionConfirmationSucceeded: () => {
        if (!pendingRef.current || pendingRef.current.abandoned) return;
        const rsp = pendingRef.current?.rsp || {};
        finish({ approved: true, info: {
          trx_seq: rsp.TrxSeqCnt, auth_code: rsp.AuthC, brand: rsp.CardAppLabel || rsp.AppLabel || rsp.CardBrand,
          amount_cents: rsp.AmtAuth, terminal_id: (() => { try { return trm.getTerminalID(); } catch { return ''; } })(),
        } });
      },
      OnTransactionConfirmationFailed: () => finish({ approved: false, reason: 'Bestätigung fehlgeschlagen – Betrag wurde nicht belastet' }),
      OnTransactionConfirmationTimedOut: () => finish({ approved: false, reason: 'Bestätigung Zeitüberschreitung – bitte Terminal-Journal prüfen', uncertain: true }),
      OnTransactionDeclined: () => finish({ approved: false, reason: 'Karte abgelehnt' }),
      OnTransactionAborted: () => finish({ approved: false, reason: 'Zahlung abgebrochen' }),
      OnTransactionReferred: () => finish({ approved: false, reason: 'Karte verlangt Rückruf beim Herausgeber – bitte andere Karte' }),
      OnTransactionTimedOut: () => finish({ approved: false, reason: 'Zeitüberschreitung am Terminal – bitte Terminal-Journal prüfen', uncertain: true }),
      OnError: (msg) => setLastError(String(msg || '')),
    });
    trmRef.current = trm;
    return trm;
  }, [finish]);

  useEffect(() => { if (paired) build(); }, [paired, build]);

  const pair = useCallback((code, name) => new Promise((resolve) => {
    const trm = build();
    pendingRef.current = { kind: 'pair', resolve };
    try { trm.pair(String(code).trim(), name || 'Padelarena Kasse'); } catch (e) { pendingRef.current = null; resolve({ ok: false, error: String(e) }); }
    setTimeout(() => { if (pendingRef.current?.kind === 'pair') { pendingRef.current = null; resolve({ ok: false, error: 'Keine Antwort vom Terminal' }); } }, 30000);
  }), [build]);

  const unpair = useCallback(() => {
    try { trmRef.current?.unpair(); } catch { /* noop */ }
    trmRef.current = null;
    localStorage.removeItem(STORAGE_KEY);
    setPaired(false); setConnected(false); setModel('');
  }, []);

  // Sends the amount to the terminal; resolves only once the payment is confirmed or failed.
  const charge = useCallback((amountChf, reference) => new Promise((resolve) => {
    const trm = build();
    if (!connected) { resolve({ approved: false, reason: 'Terminal nicht verbunden' }); return; }
    if (pendingRef.current) { resolve({ approved: false, reason: 'Terminal ist beschäftigt' }); return; }
    const cents = Math.round(Number(amountChf) * 100);
    const start = () => {
      try {
        trm.startTransaction({
          TrxFunction: trm.TransactionFunctions.PURCHASE, TrxCurrC: 756, AmtAuth: cents,
          ...(reference ? { RecOrderRef: { OrderID: String(reference).slice(0, 30) } } : {}),
        });
      } catch (e) { finish({ approved: false, reason: String(e) }); }
    };
    pendingRef.current = { kind: 'charge', resolve, start, waitActivation: false };
    setChargeAmount(cents / 100);
    setBusy(true);
    const shiftOpen = (trm.getStatus() & trm.StatusFlags.SHIFT_OPEN) !== 0;
    if (!shiftOpen) { pendingRef.current.waitActivation = true; trm.activate(); } else start();
  }), [build, connected, finish]);

  const abort = useCallback(() => {
    const p = pendingRef.current;
    if (!p || p.kind !== 'charge') return;
    p.abandoned = true;
    try { trmRef.current?.abortTransaction({}); } catch { /* noop */ }
    // Terminal may not answer (e.g. still activating) — release the Kasse after a short wait.
    setTimeout(() => { if (pendingRef.current === p) finish({ approved: false, reason: 'Zahlung abgebrochen' }); }, 4000);
  }, [finish]);

  return (
    <TerminalCtx.Provider value={{ paired, connected, busy, lastError, model, pair, unpair, charge, abort }}>
      {children}
      {busy && createPortal(
        <div className="fixed inset-0 z-[9999] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ pointerEvents: 'auto' }} data-testid="terminal-charge-overlay">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center space-y-4 shadow-2xl">
            <div className="mx-auto w-14 h-14 rounded-full border-4 border-emerald-200 border-t-emerald-600 animate-spin" />
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Kartenterminal</p>
            <p className="text-3xl font-black text-slate-900" data-testid="terminal-charge-amount">CHF {chargeAmount.toFixed(2)}</p>
            <p className="text-sm text-slate-600">Bitte Karte am Terminal vorweisen. Gebucht wird erst, wenn das Terminal die Zahlung bestätigt.</p>
            <button onClick={abort} className="w-full py-3 rounded-xl border-2 border-red-200 text-red-600 font-bold hover:bg-red-50 transition-colors" data-testid="terminal-charge-abort">
              Abbrechen
            </button>
          </div>
        </div>,
        document.body,
      )}
    </TerminalCtx.Provider>
  );
};

export const useCardTerminal = () => useContext(TerminalCtx);

// Returns { ok, info?, manual? }. Without a paired terminal the card amount is recorded manually as before.
export const useCardPayment = () => {
  const t = useCardTerminal();
  return useCallback(async (amountChf, reference) => {
    if (!t?.paired || !(Number(amountChf) > 0)) return { ok: true, manual: true };
    const r = await t.charge(amountChf, reference);
    return r.approved ? { ok: true, info: r.info } : { ok: false, reason: r.reason, uncertain: r.uncertain };
  }, [t]);
};
